import { UserContext, UserPermissions } from "@/context/User/UserContext";
import React, { useContext } from "react";
import { BsFileEarmarkCheckFill, BsFileEarmarkLock2Fill } from "react-icons/bs";
import { FaFileInvoiceDollar } from "react-icons/fa";
import { FaMoneyBillTransfer, FaUserTie } from "react-icons/fa6";
import { HiUsers } from "react-icons/hi";
import { MdEditDocument } from "react-icons/md";
import { RiDashboardFill } from "react-icons/ri";
import { TbBriefcaseFilled, TbCalendarDollar } from "react-icons/tb";
import { NavLink } from "react-router-dom";
import { Skeleton } from "../ui/skeleton";

type MenuLink = {
	key: string;
	name: string;
	url: string;
	icon: React.ReactNode;
};

const menuLinks: MenuLink[] = [
	{
		key: "dashboard",
		name: "My Dashboard",
		url: "/",
		icon: <RiDashboardFill className="text-2xl" />,
	},
	{
		key: "manager-approvals",
		name: "Departmental Approvals",
		url: "/managerapprovelist",
		icon: <FaUserTie className="text-2xl" />,
	},
	{
		key: "external-contributor",
		name: "Add External Contributor",
		url: "/create-external-contributor",
		icon: <FaUserTie className="text-2xl" />,
	},
	{
		key: "manager-secondary-approvals",
		name: "Secondary Approvals",
		url: "/managersecondaryapprovals",
		icon: <FaUserTie className="text-2xl invisible" />,
	},
	{
		key: "finance-approvals",
		name: "Finance Approvals",
		url: "/financeapprovelist",
		icon: <BsFileEarmarkCheckFill className="text-2xl" />,
	},
	{
		key: "finance-secondary-approvals",
		name: "Secondary Approvals",
		url: "/financesecondaryapprovals",
		icon: <BsFileEarmarkCheckFill className="text-2xl invisible" />,
	},
	{
		key: "admin",
		name: "Admin",
		url: "/admin",
		icon: <BsFileEarmarkLock2Fill className="text-2xl" />,
	},
	{
		key: "reports",
		name: "Reports",
		url: "/allclaims",
		icon: <FaFileInvoiceDollar className="text-2xl" />,
	},
	{
		key: "release-payments",
		name: "Release Payments",
		url: "/release-payments",
		icon: <FaMoneyBillTransfer className="text-2xl" />,
	},
	{
		key: "cash-office-reports",
		name: "Payment Reports",
		url: "/paidclaims",
		icon: <TbCalendarDollar className="text-2xl" />,
	},
	{
		key: "system-users",
		name: "System Users",
		url: "/allusers",
		icon: <HiUsers className="text-2xl" />,
	},
	{
		key: "create-job",
		name: "Create Job Authorization",
		url: "/createjob",
		icon: <MdEditDocument className="text-2xl" />,
	},
	{
		key: "finance-job-approvals",
		name: "Finance Job Approvals",
		url: "/finance-job-approvals",
		icon: <TbBriefcaseFilled className="text-2xl" />,
	},
	{
		key: "hr-partner-job-approvals",
		name: "HR Partner Job Approvals",
		url: "/hr-partner-job-approvals",
		icon: <TbBriefcaseFilled className="text-2xl" />,
	},
	{
		key: "hr-manager-job-approvals",
		name: "HR Manager Job Approvals",
		url: "/hr-manager-job-approvals",
		icon: <TbBriefcaseFilled className="text-2xl" />,
	},
	{
		key: "cfo-job-approvals",
		name: "CFO Job Approvals",
		url: "/cfo-job-approvals",
		icon: <TbBriefcaseFilled className="text-2xl" />,
	},
	{
		key: "all-job-approvals",
		name: "All Job Approvals",
		url: "/all-job-approvals",
		icon: <TbBriefcaseFilled className="text-2xl" />,
	},
];

const accessLevels: { [key: string]: string[] } = {
	"5": [
		"dashboard",
		"external-contributor",
		"my-invoices",
		"raise-claim",
		"raise-diem-claim",
		"manager-approvals",
		"finance-approvals",
		"admin",
		"reports",
		"release-payments",
		"cash-office-reports",
		"system-users",
	],
	"4": [
		"dashboard",
		"my-invoices",
		"raise-claim",
		"raise-diem-claim",
		"manager-approvals",
		"release-payments",
		"cash-office-reports",
	],
	"1": ["dashboard", "my-invoices", "raise-claim", "manager-approvals"],
	"3": [
		"dashboard",
		"my-invoices",
		"raise-claim",
		"raise-diem-claim",
		"manager-approvals",
		"finance-approvals",
		"reports",
	],
	"6": [
		"dashboard",
		"my-invoices",
		"raise-claim",
		"raise-diem-claim",
		"manager-approvals",
		"finance-approvals",
		"reports",
	],
	"7": [
		"dashboard",
		"my-invoices",
		"raise-claim",
		"raise-diem-claim",
		"manager-approvals",
		"finance-approvals",
		"reports",
	],
	"2": ["dashboard", "my-invoices", "raise-claim", "manager-approvals"],
	"34": ["dashboard", "my-invoices", "raise-claim", "manager-approvals"],
};

const jobJustificationLevels: { [key: string]: string[] } = {
	"5": ["create-job", "finance-job-approvals", "all-job-approvals"],
	"6": ["create-job", "hr-partner-job-approvals", "all-job-approvals"],
	"9": ["create-job", "hr-manager-job-approvals", "all-job-approvals"],
	"7": [
		"create-job",
		"finance-job-approvals",
		"hr-partner-job-approvals",
		"hr-manager-job-approvals",
		"cfo-job-approvals",
		"all-job-approvals",
	],
};

function extractPermissionIds(permissions: UserPermissions[]): string[] {
	return permissions.map((p) => p.permission_id.toString());
}

function getUniqueJobJustificationValues(keys: string[]): string[] {
	const allValues = keys.map((key) => jobJustificationLevels[key] || []).flat();
	return Array.from(new Set(allValues));
}

const Menu: React.FC<{ onClick?: () => void }> = ({ onClick }) => {
	const user = useContext(UserContext);

	console.log("User in Menu:", user);

	const getUserMenu = (
		userLevel: number,
		menuLinks: MenuLink[],
		accessLevels: { [key: string]: string[] }
	): MenuLink[] => {
		if (accessLevels.hasOwnProperty(userLevel)) {
			return menuLinks.filter((item) =>
				accessLevels[userLevel].includes(item.key)
			);
		}
		return [];
	};

	if (!user || !user.check)
		return (
			<ul className="flex flex-col gap-2">
				{Array.from({ length: 3 }).map((_, i) => (
					<Skeleton key={i} className="w-full h-10 bg-white" />
				))}
			</ul>
		);

	const userMenu: MenuLink[] = getUserMenu(
		user.check.usertypeid,
		menuLinks,
		accessLevels
	);

	const permissionIds = user?.permissions
		? extractPermissionIds(user.permissions)
		: [];

	const userJobJustificationKeys =
		getUniqueJobJustificationValues(permissionIds);

	console.log("User Job Justification Keys:", userJobJustificationKeys);

	const userPMenu: MenuLink[] = menuLinks.filter((item) =>
		userJobJustificationKeys.includes(item.key)
	);

	return (
		<ul className="flex flex-col gap-2">
			{userMenu.map((u) => (
				<li key={u.key} onClick={onClick}>
					<NavLink to={u.url} className="sidebar-link">
						{u.icon}
						<span>{u.name}</span>
					</NavLink>
				</li>
			))}
			{userPMenu && userPMenu.length > 0 && (
				<>
					<hr />
					<span className="font-light uppercase text-sm text-zinc-500">
						Job Justification
					</span>
					{userPMenu.map((u) => (
						<li key={u.key} onClick={onClick}>
							<NavLink to={u.url} className="sidebar-link">
								{u.icon}
								<span>{u.name}</span>
							</NavLink>
						</li>
					))}
				</>
			)}
		</ul>
	);
};

export default Menu;
